import React from 'react';
import { FlexRow } from '../style';
import { Container, AlertText, GraphContainer, NormalText } from './style';
import { Title } from '@gerdau/hefesto/';
import { Tag, KeyValueItem, SimpleGraph } from '@gerdau/hefesto-mais-web/';
import { EMeasureUnit } from '../../../../../store/commomTypes';

export type CartHeaderGraph = {
  cartQty: number;
  minQty: number;
  type: string;
  name: string;
  unit: EMeasureUnit;
  partial?: boolean;
  hasOrderItem?: boolean;
};

interface CartHeaderProps {
  productsQuantity: number;
  graphs: CartHeaderGraph[];
  loading: boolean;
  showMinimumAlert: boolean;
  showMaximumAlert: boolean;
  showPartialAlert: boolean;
}

const GraphMinimumItem = ({ graph }: { graph: CartHeaderGraph }) => {
  const percentage = (graph.cartQty * 100) / graph.minQty;
  const qty =
    graph.cartQty % 1 === 0
      ? graph.cartQty.toFixed(0)
      : graph.cartQty.toFixed(1);
  return (
    <GraphContainer complete={percentage >= 100}>
      <KeyValueItem title={graph.name.toUpperCase()}>
        <SimpleGraph percentage={(graph.cartQty * 100) / graph.minQty}>
          {`${qty}${
            percentage < 100 ? ` de ${graph.minQty}${graph.unit}` : graph.unit
          }`}
        </SimpleGraph>
      </KeyValueItem>
    </GraphContainer>
  );
};

const Badge = ({
  productsQuantity,
  loading
}: {
  productsQuantity: number;
  loading: boolean;
}) => {
  if (loading) {
    return null;
  }
  return (
    <Tag variant={productsQuantity < 30 ? 'light' : 'danger'}>
      {productsQuantity} produtos
    </Tag>
  );
};

const GraphsHeader = ({
  graphs
}: {
  loading: boolean;
  graphs: CartHeaderGraph[];
}) => {
  return (
    <FlexRow>
      {graphs.map((graph) => {
        const itemKey = JSON.stringify({
          type: graph.type,
          unit: graph.unit,
          quantity: graph.cartQty
        });

        return <GraphMinimumItem graph={graph} key={itemKey} />;
      })}
    </FlexRow>
  );
};

const PartialAlert = ({ show }: { show?: boolean }) => {
  if (!show) {
    return null;
  }

  return (
    <NormalText>
      Alguns itens da sua carteira de pedidos preencheram parte da quantidade
      mínima
    </NormalText>
  );
};

const MinimunAlert = ({ show }: { show?: boolean }) => {
  if (!show) {
    return null;
  }

  return (
    <AlertText>
      Seu carrinho precisa de uma quantidade mínima de produtos para avançar
      para revisão. Aumente a quantidade ou adicione mais produtos.
    </AlertText>
  );
};

const MaximumAlert = ({ show }: { show?: boolean }) => {
  if (!show) {
    return null;
  }

  return (
    <AlertText>
      Seu carrinho atingiu o limite de 30 produtos. Conclua este pedido e faça
      outro com mais produtos.
    </AlertText>
  );
};

const CartHeader = ({
  productsQuantity,
  graphs,
  loading,
  showMinimumAlert,
  showMaximumAlert,
  showPartialAlert
}: CartHeaderProps) => {
  return (
    <Container>
      <FlexRow>
        <Title level={2}>Seu carrinho</Title>
        <Badge loading={loading} productsQuantity={productsQuantity} />
      </FlexRow>
      <GraphsHeader loading={loading} graphs={graphs} />
      <MinimunAlert show={showMinimumAlert} />
      <MaximumAlert show={showMaximumAlert} />
      <PartialAlert show={showPartialAlert} />
    </Container>
  );
};

export default CartHeader;
